import type { testimonialsSection } from '../../../../app/_shared/marketing/landingContent';
import type { CSSProperties } from 'react';
import './landingRef.css';

type Item = (typeof testimonialsSection.items)[number];

type Props = {
  h2: string;
  lead?: string;
  items: readonly Item[];
};

function WhyCardIcon({ id }: { id: Item['id'] }) {
  // No blank-message paralysis — prompt / chat bubble
  if (id === 'prompt') {
    return (
      <svg className="why-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M5 6h14v9H9l-4 3V6Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M9 10h6M9 13h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }
  // No room full of strangers — three people / small group
  if (id === 'smallGroup') {
    return (
      <svg className="why-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M3 19c0-3.3 2.7-6 6-6s6 2.7 6 6"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="18" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" opacity="0.55" />
        <path
          d="M15.5 19c0-2.2 1.1-4.1 2.5-5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          opacity="0.55"
        />
      </svg>
    );
  }
  // Nothing you build gets thrown away — upward trend / growth chart
  if (id === 'history') {
    return (
      <svg className="why-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
        <polyline
          points="4,17 9,11 13,14 20,7"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16 7h4v4"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.65"
        />
      </svg>
    );
  }
  // No rejection to sit through — two overlapping circles / mutual connection
  if (id === 'mutual') {
    return (
      <svg className="why-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="9" cy="12" r="5.5" stroke="currentColor" strokeWidth="1.5" opacity="0.7" />
        <circle cx="15" cy="12" r="5.5" stroke="currentColor" strokeWidth="1.5" opacity="0.7" />
        <path
          d="M12 8.2c1 .9 1.7 2.3 1.7 3.8s-.7 2.9-1.7 3.8c-1-.9-1.7-2.3-1.7-3.8s.7-2.9 1.7-3.8Z"
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.45"
        />
      </svg>
    );
  }
  // No endless texting that goes nowhere — map pin / meetup location
  if (id === 'offline') {
    return (
      <svg className="why-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 2C8.7 2 6 4.7 6 8c0 4.5 6 12 6 12s6-7.5 6-12c0-3.3-2.7-6-6-6Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <circle cx="12" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" opacity="0.65" />
      </svg>
    );
  }
  return (
    <svg className="why-card__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M7 12l3 3 7-7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function TestimonialsSection({ h2, lead, items }: Props) {
  return (
    <section className="why-section" aria-labelledby="why-different-heading">
      <header className="why-section__header">
        <h2 id="why-different-heading" className="ml-heading-sans ml-text-heading">
          {h2}
        </h2>
        {lead ? <p className="why-section__lead ml-text-body-secondary">{lead}</p> : null}
      </header>
      <div className="why-grid">
        {items.map((item, index) => (
          <article
            key={item.id}
            className="why-card ml-revealStaggerItem"
            style={{ '--reveal-i': index } as CSSProperties}>
            <WhyCardIcon id={item.id} />
            <h3 className="ml-heading-sans">{item.title}</h3>
            <p className="ml-text-body-secondary why-card__body">{item.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}
